import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { BalanceGroupDTO, BalancesResponse, BillDTO } from '@six-pockets/shared';
import { billsApi } from '@/api';
import { EyeIcon, EyeOffIcon, SearchIcon } from '@/components/icons';
import { PageHeader } from '@/components/layout/PageHeader';
import { DatePicker, InlineAlert, Tabs, TextField, type TabOption } from '@/components/ui';
import { BalanceGroupCard } from '@/features/bills/components/BalanceGroupCard';
import { BillDetailsModal } from '@/features/bills/components/BillDetailsModal';
import { CollectPaymentModal } from '@/features/bills/components/CollectPaymentModal';
import { RecoveredView } from '@/features/bills/components/RecoveredView';
import { useAuth } from '@/context/AuthContext';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useReveal } from '@/hooks/useReveal';
import { cx } from '@/utils/cx';
import { todayKey } from '@/utils/date';
import { formatINR } from '@/utils/format';
import styles from '@/styles/pages/BalanceBillsPage/style.scss';

const messageOf = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

/** Total outstanding. For the club manager it reads "₹ ••••" until tapped, then shows for 3 seconds. */
function OutstandingCard({ total, masked }: { total: number; masked: boolean }) {
  const [shown, toggle] = useReveal();

  if (!masked) {
    return (
      <div className={styles.outstanding}>
        <span className={styles.statLabel}>Total outstanding</span>
        <span className={styles.outstandingValue}>{formatINR(total)}</span>
      </div>
    );
  }

  // A second tap hides it again before the 3 seconds are up.
  return (
    <button
      type="button"
      className={cx(styles.outstanding, styles.masked)}
      aria-pressed={shown}
      aria-label={shown ? `Total outstanding: ${formatINR(total)}. Tap to hide` : 'Total outstanding: hidden. Tap to show'}
      onClick={toggle}
    >
      <span className={styles.labelRow}>
        <span className={styles.statLabel}>Total outstanding</span>
        {shown ? <EyeIcon size={16} /> : <EyeOffIcon size={16} />}
      </span>
      <span className={cx(styles.outstandingValue, !shown && styles.hidden)} aria-hidden>
        {shown ? formatINR(total) : '₹ ••••'}
      </span>
    </button>
  );
}

/**
 * Every bill with money still due, from any day, grouped by customer (biggest balance first). The summary shows
 * the total outstanding; each customer has Pay now (collect all or part of their balance, oldest bill first) and
 * Details (their bills, each with View).
 */
function OwedView() {
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search.trim(), 300);
  const [reloadKey, setReloadKey] = useState(0);

  const [result, setResult] = useState<BalancesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [paying, setPaying] = useState<BalanceGroupDTO | null>(null);
  const [viewing, setViewing] = useState<BillDTO | null>(null);
  // The club manager (no reports module) sees the money due hidden until tapped; admin sees it plainly.
  const masked = !useAuth().can('reports');

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    billsApi.balances({ q: query || undefined }, ctrl.signal).then(
      (r) => {
        setResult(r);
        setLoading(false);
      },
      (err) => {
        if (ctrl.signal.aborted) return;
        setError(messageOf(err));
        setLoading(false);
      },
    );
    return () => ctrl.abort();
  }, [query, reloadKey]);

  /** After a payment: reload the balances. */
  const afterPaid = useCallback(() => {
    setPaying(null);
    setReloadKey((k) => k + 1);
  }, []);

  const groups = result?.groups ?? [];

  return (
    <>
      {result && (
        <section className={styles.summary} aria-label="Outstanding">
          <OutstandingCard total={result.totalDue} masked={masked} />
          <div className={styles.stat}>
            <span className={styles.statLabel}>Customers</span>
            <span className={styles.statValue}>{result.groupCount}</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Bills</span>
            <span className={styles.statValue}>{result.billCount}</span>
          </div>
        </section>
      )}

      {(result?.groupCount ?? 0) > 0 && (
        <div className={styles.toolbar}>
          <TextField
            size="md"
            aria-label="Search balances by customer, mobile or bill number"
            placeholder="Search customer, mobile, bill #"
            prefix={<SearchIcon size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.search}
          />
        </div>
      )}

      {error && <InlineAlert message={error} onDismiss={() => setError(null)} />}

      {!result && loading ? (
        <p className={styles.empty}>Loading balances…</p>
      ) : groups.length === 0 ? (
        <p className={styles.empty}>{query ? 'No balances match your search' : 'Nothing outstanding. Every bill is paid.'}</p>
      ) : (
        <ul className={styles.list} aria-busy={loading}>
          {groups.map((g) => (
            <BalanceGroupCard key={g.customer?.id ?? g.bills[0]!.id} group={g} masked={masked} onPay={setPaying} onView={setViewing} />
          ))}
        </ul>
      )}

      {paying && <CollectPaymentModal group={paying} onPaid={afterPaid} onClose={() => setPaying(null)} />}
      {viewing && <BillDetailsModal key={viewing.id} bill={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

type View = 'owed' | 'recovered';

const VIEW_OPTIONS: TabOption<View>[] = [
  { value: 'owed', label: 'Owed' },
  { value: 'recovered', label: 'Recovered' },
];

/**
 * Balance bills: Owed (what customers owe now, Pay now) and Recovered (the balances paid back on a day, bill by bill).
 * The tab and day are in the URL (?view=recovered&date=YYYY-MM-DD), so Reports can link to a day's recoveries.
 */
export function BalanceBillsPage() {
  const [params, setParams] = useSearchParams();
  const view: View = params.get('view') === 'recovered' ? 'recovered' : 'owed';
  const date = params.get('date') ?? todayKey();
  const [viewing, setViewing] = useState<BillDTO | null>(null);

  const changeView = (v: View) => setParams(v === 'owed' ? {} : { view: v, date }, { replace: true });

  return (
    <>
      <PageHeader title="Balance bills" />
      <Tabs
        label="Balance bills"
        options={VIEW_OPTIONS}
        value={view}
        onChange={changeView}
        aside={
          view === 'recovered' && (
            <DatePicker label="Day recovered" value={date} onChange={(d) => setParams({ view: 'recovered', date: d }, { replace: true })} />
          )
        }
      />
      {view === 'owed' ? <OwedView /> : <RecoveredView date={date} onView={setViewing} />}
      {viewing && <BillDetailsModal key={viewing.id} bill={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}
